import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircle, Check, Pencil } from "lucide-react";
import panelApi from "@/lib/panelApi";
import { apiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { buildThirteenthReceipts, receiptFileName } from "@/lib/thirteenthReceipt";
import { shareFiles } from "@/lib/payrollXlsx";

export const usePhones = () =>
  useQuery({ queryKey: ["panel-phones"], queryFn: async () => (await panelApi.get("/panel/phones")).data?.phones || {} });

export const fmtPhone = (p) => (p ? `(${p.slice(2, 4)}) ${p.slice(4, -4)}-${p.slice(-4)}` : "");

const send = (r, phone, year, parcela, logo) => {
  const name = receiptFileName(r.name, year, parcela);
  const blob = buildThirteenthReceipts([r], year, parcela, logo).output("blob");
  const text = `Olá, ${r.name}! Segue seu recibo da ${parcela}ª parcela do 13º salário ${year}. Valor líquido: ${brl(r.net)}. Por favor, assine e devolva.`;
  shareFiles([{ blob, name }], text, `https://wa.me/${phone || ""}?text=${encodeURIComponent(text)}`);
};

function PhoneDialog({ row, phone, onClose, onSaved }) {
  const qc = useQueryClient();
  const [v, setV] = useState(phone ? fmtPhone(phone) : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await panelApi.put(`/panel/phone/${row.user_id}`, { phone: v });
      await qc.invalidateQueries({ queryKey: ["panel-phones"] });
      onSaved(data.phone);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm border-slate-800 bg-slate-900 text-slate-100" data-testid="phone-dialog">
        <DialogHeader>
          <DialogTitle>WhatsApp de {row.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Fica salvo para os próximos envios.</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="(85) 99999-9999"
          className="border-slate-700 bg-slate-950 text-slate-100" data-testid="phone-input" />
        <Button className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy || !v.trim()} onClick={save} data-testid="phone-save">
          <MessageCircle className="h-4 w-4" /> Salvar e enviar recibo
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// Envio individual do recibo do 13º: abre a conversa do colaborador com a mensagem e baixa o PDF para anexar.
export function ReceiptWhatsApp({ row, phones, sent, onSent, year, parcela, logo }) {
  const [editing, setEditing] = useState(false);
  const phone = phones?.[row.user_id];
  const go = (p) => { setEditing(false); send(row, p, year, parcela, logo); onSent(row.user_id); };
  return (
    <div className="inline-flex items-center gap-0.5">
      <Button size="sm" variant="ghost" className={sent ? "text-emerald-300" : "text-slate-300 hover:text-emerald-300"} title={phone ? fmtPhone(phone) : "Cadastrar WhatsApp"}
        onClick={() => (phone || navigator.canShare ? go(phone) : setEditing(true))} data-testid={`thirteenth-wa-${row.user_id}`}>
        {sent ? <Check className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />} {sent ? "Enviado" : "WhatsApp"}
      </Button>
      {phone && (
        <button className="rounded p-1 text-slate-500 hover:text-slate-200" title="Alterar WhatsApp" onClick={() => setEditing(true)} data-testid={`thirteenth-wa-edit-${row.user_id}`}>
          <Pencil className="h-3 w-3" />
        </button>
      )}
      {editing && <PhoneDialog row={row} phone={phone} onClose={() => setEditing(false)} onSaved={go} />}
    </div>
  );
}
